"use client";

import axiosInstance from "@/app/api";
import {DashboardReportFilter} from "@/components/@filter";
import {NeoSelect} from "@/components/forms";
import {ColumnIcon, DownloadIcon, SearchIcon} from "@/components/shared";
import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {useNeoContext} from "@/context/neo/neo.context";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  SelectItem,
  Spinner,
  Tooltip
} from "@nextui-org/react";
import Image from "next/image";
import {Fragment, ReactNode, useEffect, useMemo, useState} from "react";
import {FaLayerGroup} from "react-icons/fa";
import {
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiEye,
  FiTruck,
  FiUserCheck,
  FiUserX,
  FiUsers
} from "react-icons/fi";
import {IoInformationCircle} from "react-icons/io5";

type AttendanceUserDetail = {
  name: string;
  admissionNo: string;
  vehicleName: string;
  onwardPickupSwipe: string;
  onwardDropSwipe: string;
  returnPickup: string;
  returnDrop: string;
  otherSwipes: string;
};

type AttendanceSummary = {
  totalVehicles: number;
  totalUsers: number;
  presentUsers: number;
  unAssignedUsers: number;
  absentUsers: number;
  attendancePercentage: number;
  presentUsersList?: AttendanceUserDetail[];
  unAssignedUsersList?: AttendanceUserDetail[];
  absentUsersList?: AttendanceUserDetail[];
};

type AttendanceDate = {
  date: number;
  dateLabel: string;
  assignedUsers: number;
  presentUsers: number;
  unAssignedUsers: number;
  absentUsers: number;
  attendancePercentage: number;
};

type AttendanceVehicle = {
  vehicleName: string;
  assignedUsers: number;
  presentUsers: number;
  unAssignedUsers: number;
  absentUsers: number;
  attendancePercentage: number;
  dates: AttendanceDate[];
};

type AttendanceDateGroup = AttendanceDate & {
  vehicles: Array<AttendanceVehicle & {dateSummary: AttendanceDate}>;
};

type GroupMode = "vehicle-date" | "date-vehicle";
type AttendanceColumnKey =
  | "dateSummary"
  | "assignedUsers"
  | "presentUsers"
  | "unAssignedUsers"
  | "absentUsers"
  | "attendancePercentage"
  | "status";

const configurableTableColumns: Array<{
  align?: "center" | "left" | "right";
  key: AttendanceColumnKey;
  label: string;
}> = [
  {key: "dateSummary", label: "Date Range Summary"},
  {align: "right", key: "assignedUsers", label: "Assigned Users"},
  {align: "right", key: "presentUsers", label: "Present Users"},
  {align: "right", key: "unAssignedUsers", label: "Un Assigned Users"},
  {align: "right", key: "absentUsers", label: "Absent Users"},
  {key: "attendancePercentage", label: "Attendance %"}
  //{key: "status", label: "Trend / Status"}
];

const tableHeaderDescriptions: Partial<Record<AttendanceColumnKey, ReactNode>> =
  {
    assignedUsers: "Users assigned to the selected vehicle/date group.",
    presentUsers:
      "Assigned users who have at least one bus swipe in the selected vehicle/date group.",
    unAssignedUsers:
      "Users who swiped in the selected vehicle/date group but are not assigned to it.",
    absentUsers:
      "Assigned users without a bus swipe. Calculation: Assigned Users - Present Users."
  };

const emptySummary: AttendanceSummary = {
  totalVehicles: 0,
  totalUsers: 0,
  presentUsers: 0,
  unAssignedUsers: 0,
  absentUsers: 0,
  attendancePercentage: 0
};
const groupingFilter = [
  {
    id: "vehicle-date",
    label: "Grouped by: Vehicle -> Date"
  },
  {
    id: "date-vehicle",
    label: "Grouped by: Date -> Vehicle"
  }
];
const numberFormatter = new Intl.NumberFormat("en-US");

const toNumber = (value: unknown) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatNumber = (value: unknown) =>
  numberFormatter.format(toNumber(value));

const formatPercentage = (value: unknown) => `${toNumber(value).toFixed(2)}%`;

const formatDateRangeLabel = (from?: number, to?: number) => {
  if (!from || !to) return "Selected date range";

  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  });
  const fromLabel = formatter.format(new Date(from));
  const toLabel = formatter.format(new Date(to));

  return fromLabel === toLabel ? fromLabel : `${fromLabel} - ${toLabel}`;
};

const getDayCountLabel = (count: number) =>
  `${count} ${count === 1 ? "day" : "days"}`;

const getRecordCount = (vehicles: AttendanceVehicle[]) =>
  vehicles.reduce(
    (count, vehicle) => count + Math.max(vehicle.dates.length, 1),
    0
  );

const getUserDetailKey = (user: AttendanceUserDetail) =>
  user.admissionNo || `${user.name}-${user.vehicleName}`;

const mergeUniqueUserDetails = (
  ...lists: Array<AttendanceUserDetail[] | undefined>
) => {
  const merged = new Map<string, AttendanceUserDetail>();

  lists.forEach(list => {
    list?.forEach(user => {
      const key = getUserDetailKey(user);
      if (!merged.has(key)) merged.set(key, user);
    });
  });

  return Array.from(merged.values());
};

const getBranchIds = (dashboardfilter: any, fallbackBranch?: string | null) => {
  const branches = dashboardfilter?.instituteuserids;
  if (Array.isArray(branches) && branches.length > 0) return branches;
  return fallbackBranch ? [fallbackBranch] : [];
};

const buildDefaultRequest = ({
  dashboardfilter,
  filterContext,
  offset
}: {
  dashboardfilter: any;
  filterContext: any;
  offset: string;
}) => {
  const fromDate = new Date();
  const toDate = new Date();
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);

  return {
    userId: filterContext.client.institutionUserId,
    instituteId: filterContext.client.institutionId,
    pageNumber: 1,
    itemsPerPage: 100000,
    searchBy: "",
    sortColumn: "",
    sortValue: "",
    from: fromDate.getTime(),
    to: toDate.getTime(),
    filterType: "1",
    offset,
    staffClassId: [],
    filters: {
      branch: getBranchIds(
        dashboardfilter,
        filterContext.client.institutionUserId
      ),
      trackers: [],
      status: []
    }
  };
};

const getAttendanceStatus = (percentage: number) => {
  if (percentage >= 90 && percentage <= 100) {
    return {
      label: "Excellent",
      className: "bg-[#e8f8ee] text-[#16833a]"
    };
  }
  if (percentage >= 70) {
    return {
      label: "Very Good",
      className: "bg-[#eaf1ff] text-[#2563eb]"
    };
  }
  if (percentage >= 50) {
    return {
      label: "Good",
      className: "bg-[#fff7e8] text-[#b75c00]"
    };
  }

  return {
    label: "Need Improvement",
    className: "bg-[#fff0f2] text-[#f04455]"
  };
};

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

const buildDateGroups = (vehicles: AttendanceVehicle[]) => {
  const dateGroups = new Map<number, AttendanceDateGroup>();

  vehicles.forEach(vehicle => {
    vehicle.dates.forEach(dateSummary => {
      const dateKey = dateSummary.date;
      const current = dateGroups.get(dateKey) ?? {
        date: dateSummary.date,
        dateLabel: dateSummary.dateLabel,
        assignedUsers: 0,
        presentUsers: 0,
        unAssignedUsers: 0,
        absentUsers: 0,
        attendancePercentage: 0,
        vehicles: []
      };

      current.assignedUsers += toNumber(dateSummary.assignedUsers);
      current.presentUsers += toNumber(dateSummary.presentUsers);
      current.unAssignedUsers += toNumber(dateSummary.unAssignedUsers);
      current.absentUsers += toNumber(dateSummary.absentUsers);
      current.vehicles.push({...vehicle, dateSummary});

      dateGroups.set(dateKey, current);
    });
  });

  return Array.from(dateGroups.values())
    .map(group => ({
      ...group,
      attendancePercentage:
        group.assignedUsers > 0
          ? Number(
              ((group.presentUsers / group.assignedUsers) * 100).toFixed(2)
            )
          : 0
    }))
    .sort((a, b) => b.date - a.date);
};

export const BusAttendanceOverview = ({reportdata}: {reportdata: any}) => {
  const {filterContext, offset} = useNeoContext();
  const activeReport = reportdata?.activeReport;
  const [request, setRequest] = useState<any>(null);
  const [summary, setSummary] = useState<AttendanceSummary>(emptySummary);
  const [tableData, setTableData] = useState<AttendanceVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [groupMode, setGroupMode] = useState<GroupMode>("vehicle-date");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<
    Set<AttendanceColumnKey>
  >(() => new Set(configurableTableColumns.map(column => column.key)));
  const [userDetailModal, setUserDetailModal] = useState<{
    title: string;
    rows: AttendanceUserDetail[];
  } | null>(null);

  const branchKey = (reportdata?.dashboardfilter?.instituteuserids ?? []).join(
    ","
  );

  useEffect(() => {
    if (
      !filterContext?.client?.institutionUserId ||
      !filterContext?.client?.institutionId
    ) {
      return;
    }

    setRequest(
      buildDefaultRequest({
        dashboardfilter: reportdata?.dashboardfilter,
        filterContext,
        offset
      })
    );
  }, [
    branchKey,
    filterContext,
    filterContext?.client?.institutionId,
    filterContext?.client?.institutionUserId,
    offset,
    reportdata?.dashboardfilter
  ]);

  useEffect(() => {
    if (!activeReport?.url || !request?.from || !request?.to) return;

    let isCurrentRequest = true;
    const fetchOverview = async () => {
      setIsLoading(true);
      setErrorMessage("");

      const response = await axiosInstance().post(activeReport.url, request);
      if (!isCurrentRequest) return;

      const responseData = response?.data;
      if (responseData?.status !== "success") {
        setSummary(emptySummary);
        setTableData([]);
        setErrorMessage("Unable to load bus attendance overview");
        return;
      }

      setSummary(responseData?.data?.summary ?? emptySummary);
      setTableData(responseData?.data?.tableData ?? []);
    };

    fetchOverview()
      .catch(() => {
        if (!isCurrentRequest) return;
        setSummary(emptySummary);
        setTableData([]);
        setErrorMessage("Unable to load bus attendance overview");
      })
      .finally(() => {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [activeReport?.url, request]);

  const filteredVehicles = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return tableData;

    return tableData.filter(vehicle =>
      vehicle.vehicleName.toLowerCase().includes(search)
    );
  }, [searchValue, tableData]);

  const dateGroups = useMemo(
    () => buildDateGroups(filteredVehicles),
    [filteredVehicles]
  );

  useEffect(() => {
    const keys =
      groupMode === "vehicle-date"
        ? filteredVehicles.map(vehicle => `vehicle-${vehicle.vehicleName}`)
        : dateGroups.map(group => `date-${group.date}`);

    setExpandedGroups(new Set(keys));
  }, [dateGroups, filteredVehicles, groupMode]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleVisibleColumn = (key: AttendanceColumnKey) => {
    setVisibleColumns(current => {
      const next = new Set(current);

      if (next.has(key)) {
        if (next.size === 1) return current;
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const applyFilter = (filterData: any) => {
    const nextFilters = {
      ...(request?.filters ?? {}),
      ...(filterData?.filters ?? {}),
      branch: getBranchIds(
        reportdata?.dashboardfilter,
        filterContext.client.institutionUserId
      ),
      trackers: filterData?.trackerId
        ? Array.isArray(filterData.trackerId)
          ? filterData.trackerId
          : [filterData.trackerId]
        : (request?.filters?.trackers ?? [])
    };

    setRequest((current: any) => ({
      ...current,
      ...filterData,
      filters: nextFilters,
      offset,
      staffClassId: current?.staffClassId ?? []
    }));
    setIsFilterOpen(false);
  };

  const exportRows = async () => {
    const {utils, writeFileXLSX} = await import("xlsx");
    const rows = filteredVehicles.flatMap(vehicle => {
      if (!vehicle.dates.length) {
        return [
          {
            "Vehicle Name": vehicle.vehicleName,
            Date: "",
            "Assigned Users": vehicle.assignedUsers,
            "Present Users": vehicle.presentUsers,
            "Un Assigned Users": vehicle.unAssignedUsers,
            "Absent Users": vehicle.absentUsers,
            "Attendance %": vehicle.attendancePercentage
          }
        ];
      }

      return vehicle.dates.map(dateSummary => ({
        "Vehicle Name": vehicle.vehicleName,
        Date: dateSummary.dateLabel,
        "Assigned Users": dateSummary.assignedUsers,
        "Present Users": dateSummary.presentUsers,
        "Un Assigned Users": dateSummary.unAssignedUsers,
        "Absent Users": dateSummary.absentUsers,
        "Attendance %": dateSummary.attendancePercentage
      }));
    });

    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Bus Attendance");
    writeFileXLSX(
      workbook,
      `Bus Attendance-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const uniquePresentUsersList = useMemo(
    () =>
      mergeUniqueUserDetails(
        summary.presentUsersList,
        summary.unAssignedUsersList
      ),
    [summary.presentUsersList, summary.unAssignedUsersList]
  );
  const uniquePresentUsersCount = summary.presentUsersList?.length
    ? uniquePresentUsersList.length
    : toNumber(summary.presentUsers) + toNumber(summary.unAssignedUsers);

  const uniqueAbsentUsersList = useMemo(() => {
    const unassignedKeys = new Set(
      (summary.unAssignedUsersList ?? []).map(getUserDetailKey)
    );
    return (summary.absentUsersList ?? []).filter(
      user => !unassignedKeys.has(getUserDetailKey(user))
    );
  }, [summary.absentUsersList, summary.unAssignedUsersList]);
  const uniqueAbsentUsersCount = summary.absentUsersList?.length
    ? uniqueAbsentUsersList.length
    : toNumber(summary.absentUsers);

  const rangeLabel = formatDateRangeLabel(request?.from, request?.to);
  const totalRecords = getRecordCount(tableData);
  const filteredRecordCount = getRecordCount(filteredVehicles);
  const visibleColumnCount =
    1 +
    configurableTableColumns.filter(column => visibleColumns.has(column.key))
      .length;

  return (
    <section className="flex min-h-[calc(100vh-63px)] w-full flex-col overflow-hidden bg-white px-5 py-5 text-[#1f2937]">
      <div className="flex w-full flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {activeReport?.filters?.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  aria-label="Open filters"
                  onClick={() => {
                    setIsFilterOpen(open => !open);
                    setIsColumnMenuOpen(false);
                  }}
                  className="flex h-7 w-7 items-center justify-center"
                >
                  <Image
                    src="/icons/filter.svg"
                    alt=""
                    width={20}
                    height={20}
                    className="h-5 w-5"
                  />
                </button>
                {isFilterOpen && (
                  <div className="absolute left-0 z-50 mt-2 w-auto">
                    <div className="rounded-md border bg-white p-5 shadow-lg">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        onClick={() => setIsFilterOpen(false)}
                        className="absolute right-2 top-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </Button>
                      <DashboardReportFilter
                        activeFilters={activeReport.filters}
                        innerFilters={activeReport.innerFilters}
                        oldFilter={request}
                        onSubmit={applyFilter}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-col">
              <h5 className="text-xl">
                {activeReport?.menu ?? "Bus Attendance"}
              </h5>
              <h3 className="text-xs text-gray-400">
                Bus attendance report generated from CTT and RFID swipe records.
              </h3>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-small text-default-400">
            Total Data : {totalRecords}
          </span>
          <div className="flex items-center justify-between">
            <label className="relative block w-[280px] max-w-[calc(100vw-140px)]">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#1f2937]" />
              <input
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                placeholder="Search..."
                className="h-10 w-full rounded-full border border-[#dfe1e6] bg-white pl-9 pr-3 text-sm text-[#1f2937] shadow-sm outline-none transition placeholder:text-[#667085] focus:border-[#9aa4b2]"
              />
            </label>
            <button
              type="button"
              aria-label="Export bus attendance"
              onClick={exportRows}
              className="flex h-9 w-9 cursor-pointer items-center justify-center px-2 text-[#111827]"
            >
              <DownloadIcon size={24} />
            </button>
            <div className="relative">
              <button
                type="button"
                aria-label="Show table columns"
                onClick={() => {
                  setIsColumnMenuOpen(open => !open);
                  setIsFilterOpen(false);
                }}
                className="flex h-9 w-9 cursor-pointer items-center justify-center px-2"
              >
                <ColumnIcon size={24} />
              </button>
              {isColumnMenuOpen && (
                <div className="absolute right-0 z-40 mt-2 w-56 rounded-md border bg-white p-3 shadow-lg">
                  {configurableTableColumns.map(column => (
                    <label
                      key={column.key}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold text-[#344054] hover:bg-[#f5f7fb]"
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(column.key)}
                        onChange={() => toggleVisibleColumn(column.key)}
                        className="h-4 w-4"
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <SummaryCard
            icon={<FiTruck />}
            description="Vehicles with attendance records"
            label="Total Vehicles"
            value={formatNumber(summary.totalVehicles)}
            tone="blue"
          />
          <SummaryCard
            icon={<FiUsers />}
            description="Total unique users in selected filters"
            label="Total Users"
            value={formatNumber(summary.totalUsers)}
            tone="purple"
          />
          <SummaryCard
            icon={<FiCheckCircle />}
            description="Unique users with bus swipes (assigned + unassigned)"
            label="Present Users"
            value={formatNumber(uniquePresentUsersCount)}
            tone="green"
            onView={
              uniquePresentUsersList.length
                ? () =>
                    setUserDetailModal({
                      title: "Present Users",
                      rows: uniquePresentUsersList
                    })
                : undefined
            }
          />
          <SummaryCard
            icon={<FiUserCheck />}
            description="Swipes from unassigned users"
            label="Un Assigned Users"
            value={formatNumber(summary.unAssignedUsers)}
            tone="amber"
            onView={
              summary.unAssignedUsersList?.length
                ? () =>
                    setUserDetailModal({
                      title: "Un Assigned Users",
                      rows: summary.unAssignedUsersList ?? []
                    })
                : undefined
            }
          />
          <SummaryCard
            icon={<FiUserX />}
            description="Assigned users without any swipe (assigned or unassigned)"
            label="Absent Users"
            value={formatNumber(uniqueAbsentUsersCount)}
            tone="red"
            onView={
              uniqueAbsentUsersList.length
                ? () =>
                    setUserDetailModal({
                      title: "Absent Users",
                      rows: uniqueAbsentUsersList
                    })
                : undefined
            }
          />
          <SummaryCard
            icon={<FiBarChart2 />}
            description="Present users out of assigned"
            label="Attendance %"
            value={formatPercentage(summary.attendancePercentage)}
            tone="blue"
          />
        </div>
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex rounded-lg  p-1 text-sm font-bold text-[#526078]">
            <NeoSelect
              label=""
              size="md"
              className="w-[260px]"
              placeholder="Group by"
              selectedKeys={groupMode ? [groupMode] : []}
              onSelectionChange={keys => {
                const first = Array.isArray(keys)
                  ? keys[0]
                  : Array.from(keys as Set<unknown>)[0];

                const value =
                  first !== undefined && first !== null
                    ? String(first)
                    : "class-date";

                setGroupMode(value as "vehicle-date" | "date-vehicle");
              }}
              startContent={<FaLayerGroup className="h-4 w-4 text-[#1e2a44]" />}
            >
              {groupingFilter.map(item => (
                <SelectItem key={item.id} value={item.id}>
                  {item.label}
                </SelectItem>
              ))}
            </NeoSelect>
            {/* <button
              type="button"
              onClick={() => setGroupMode("vehicle-date")}
              className={`rounded-md px-3 py-2 transition ${
                groupMode === "vehicle-date"
                  ? "bg-[#eef4ff] text-[#2563eb]"
                  : "hover:bg-[#f5f7fb]"
              }`}
            >
              Grouped by: Vehicle &gt; Date
            </button>
            <button
              type="button"
              onClick={() => setGroupMode("date-vehicle")}
              className={`rounded-md px-3 py-2 transition ${
                groupMode === "date-vehicle"
                  ? "bg-[#eef4ff] text-[#2563eb]"
                  : "hover:bg-[#f5f7fb]"
              }`}
            >
              Date &gt; Vehicle
            </button> */}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#dfe6f1] bg-white shadow-sm">
          <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left text-xs">
            <thead className="sticky top-0 z-10 bg-[#f8fafc] text-[#475467]">
              <tr>
                <TableHead>Vehicle / Date</TableHead>
                {configurableTableColumns.map(
                  column =>
                    visibleColumns.has(column.key) && (
                      <TableHead key={column.key} align={column.align}>
                        {column.key === "status" ? (
                          <TrendStatusHeader />
                        ) : tableHeaderDescriptions[column.key] ? (
                          <HeaderWithInfo
                            ariaLabel={`${column.label} calculation`}
                            content={tableHeaderDescriptions[column.key]}
                            label={column.label}
                          />
                        ) : (
                          column.label
                        )}
                      </TableHead>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={visibleColumnCount}>
                    <div className="flex min-h-64 items-center justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td colSpan={visibleColumnCount}>
                    <EmptyState message={errorMessage} />
                  </td>
                </tr>
              ) : groupMode === "vehicle-date" ? (
                <VehicleDateRows
                  expandedGroups={expandedGroups}
                  rangeLabel={rangeLabel}
                  rows={filteredVehicles}
                  toggleGroup={toggleGroup}
                  visibleColumns={visibleColumns}
                />
              ) : (
                <DateVehicleRows
                  expandedGroups={expandedGroups}
                  rows={dateGroups}
                  toggleGroup={toggleGroup}
                  visibleColumns={visibleColumns}
                />
              )}
            </tbody>
          </table>
          {!isLoading && !errorMessage && filteredRecordCount === 0 && (
            <EmptyState message="No bus attendance records found" />
          )}
        </div>
      </div>

      {userDetailModal && (
        <UserDetailModal
          isOpen={Boolean(userDetailModal)}
          onClose={() => setUserDetailModal(null)}
          rows={userDetailModal.rows}
          title={userDetailModal.title}
        />
      )}
    </section>
  );
};

const SummaryCard = ({
  description,
  icon,
  label,
  onView,
  tone,
  value
}: {
  description: string;
  icon: ReactNode;
  label: string;
  onView?: () => void;
  tone: "amber" | "blue" | "green" | "purple" | "red";
  value: string;
}) => {
  const toneStyles = {
    amber: "bg-[#fff7e8] text-[#f28a03]",
    blue: "bg-[#eef4ff] text-[#2563eb]",
    green: "bg-[#e8f8ee] text-[#16833a]",
    purple: "bg-[#f0eaff] text-[#7047d9]",
    red: "bg-[#fff0f2] text-[#f04455]"
  };

  return (
    <article className="rounded-lg border border-[#e5eaf3] bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${toneStyles[tone]} [&_svg]:h-5 [&_svg]:w-5`}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-xs font-bold text-[#667085]">
              {label}
            </p>
            {onView && (
              <button
                type="button"
                aria-label={`View ${label} details`}
                onClick={onView}
                className="flex h-6 w-6 flex-none items-center justify-center rounded-full text-[#667085] hover:bg-[#f5f7fb] hover:text-[#1f2937]"
              >
                <FiEye className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mt-1 truncate text-xl font-bold text-[#1f2937]">
            {value}
          </p>
        </div>
      </div>
      <p className="mt-3 truncate text-[11px] font-semibold text-[#98a2b3]">
        {description}
      </p>
    </article>
  );
};

const userDetailColumns: NeoColumnType[] = [
  {uid: "name", name: "Name"},
  {uid: "admissionNo", name: "Admission No"},
  {uid: "vehicleName", name: "Vehicle Name"},
  {uid: "onwardPickupSwipe", name: "Onward Pickup"},
  {uid: "onwardDropSwipe", name: "Onward Drop"},
  {uid: "returnPickup", name: "Return Pickup"},
  {uid: "returnDrop", name: "Return Drop"},
  {uid: "otherSwipes", name: "Other Swipes"}
];
const userDetailActiveColumns = userDetailColumns.map(column => column.uid);

const UserDetailModal = ({
  isOpen,
  onClose,
  rows,
  title
}: {
  isOpen: boolean;
  onClose: () => void;
  rows: AttendanceUserDetail[];
  title: string;
}) => {
  const table: NeoTableType = useMemo(
    () => ({
      columns: userDetailColumns,
      activeColumns: userDetailActiveColumns,
      tabledatas: rows,
      style: {height: "h-[60vh]"},
      meta: {
        id: 0,
        title,
        description: "Total Data",
        hasSearch: true,
        hasUpload: false,
        hasDownload: true,
        hasAdd: false,
        hasActions: false,
        hasCheckbox: false,
        hasFilter: false,
        hasHeader: true,
        hasFooter: false,
        hasPagination: true,
        hasCreate: false
      },
      url: {
        page: {},
        api: {get: ""}
      }
    }),
    [rows, title]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
      <ModalContent>
        <ModalBody className="py-6">
          <div className="h-[85vh] w-full">
            <NeoAGgrid table={table} />
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const VehicleDateRows = ({
  expandedGroups,
  rangeLabel,
  rows,
  toggleGroup,
  visibleColumns
}: {
  expandedGroups: Set<string>;
  rangeLabel: string;
  rows: AttendanceVehicle[];
  toggleGroup: (key: string) => void;
  visibleColumns: Set<AttendanceColumnKey>;
}) => {
  return (
    <>
      {rows.map(vehicle => {
        const groupKey = `vehicle-${vehicle.vehicleName}`;
        const canToggle = vehicle.dates.length > 1;
        const isExpanded = canToggle && expandedGroups.has(groupKey);

        return (
          <Fragment key={groupKey}>
            <GroupRow
              breakdownLabel={getDayCountLabel(vehicle.dates.length)}
              canToggle={canToggle}
              dateLabel={rangeLabel}
              isExpanded={isExpanded}
              metrics={vehicle}
              name={vehicle.vehicleName}
              onToggle={() => toggleGroup(groupKey)}
              visibleColumns={visibleColumns}
            />
            {canToggle &&
              isExpanded &&
              vehicle.dates.map(dateSummary => (
                <DetailRow
                  key={`${vehicle.vehicleName}-${dateSummary.date}`}
                  dateLabel={dateSummary.dateLabel}
                  metrics={dateSummary}
                  name=""
                  visibleColumns={visibleColumns}
                />
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

const DateVehicleRows = ({
  expandedGroups,
  rows,
  toggleGroup,
  visibleColumns
}: {
  expandedGroups: Set<string>;
  rows: AttendanceDateGroup[];
  toggleGroup: (key: string) => void;
  visibleColumns: Set<AttendanceColumnKey>;
}) => {
  return (
    <>
      {rows.map(group => {
        const groupKey = `date-${group.date}`;
        const canToggle = group.vehicles.length > 1;
        const isExpanded = canToggle && expandedGroups.has(groupKey);

        return (
          <Fragment key={groupKey}>
            <GroupRow
              breakdownLabel={`${group.vehicles.length} vehicles`}
              canToggle={canToggle}
              dateLabel="Daily Summary"
              isExpanded={isExpanded}
              metrics={group}
              name={group.dateLabel}
              onToggle={() => toggleGroup(groupKey)}
              visibleColumns={visibleColumns}
            />
            {canToggle &&
              isExpanded &&
              group.vehicles.map(vehicle => (
                <DetailRow
                  key={`${group.date}-${vehicle.vehicleName}`}
                  dateLabel={vehicle.dateSummary.dateLabel}
                  metrics={vehicle.dateSummary}
                  name={vehicle.vehicleName}
                  visibleColumns={visibleColumns}
                />
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

const GroupRow = ({
  breakdownLabel,
  canToggle = true,
  dateLabel,
  isExpanded,
  metrics,
  name,
  onToggle,
  visibleColumns
}: {
  breakdownLabel: string;
  canToggle?: boolean;
  dateLabel: string;
  isExpanded: boolean;
  metrics: Omit<AttendanceVehicle, "dates" | "vehicleName">;
  name: string;
  onToggle: () => void;
  visibleColumns: Set<AttendanceColumnKey>;
}) => {
  return (
    <tr className="bg-white text-[#344054] hover:bg-[#f8fafc]">
      <TableCell>
        {canToggle ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 items-center gap-2 font-bold"
          >
            {isExpanded ? (
              <FiChevronDown className="h-4 w-4" />
            ) : (
              <FiChevronRight className="h-4 w-4" />
            )}
            <span title={name} className="block max-w-[220px] truncate">
              {name}
            </span>
          </button>
        ) : (
          <div className="flex min-w-0 items-center font-bold">
            <span title={name} className="block max-w-[220px] truncate">
              {name}
            </span>
          </div>
        )}
      </TableCell>
      {visibleColumns.has("dateSummary") && (
        <TableCell>
          <div className="font-bold">{dateLabel}</div>
          <div className="text-[11px] font-semibold text-[#98a2b3]">
            {breakdownLabel}
          </div>
        </TableCell>
      )}
      <MetricCells metrics={metrics} visibleColumns={visibleColumns} />
    </tr>
  );
};

const DetailRow = ({
  dateLabel,
  metrics,
  name,
  visibleColumns
}: {
  dateLabel: string;
  metrics: AttendanceDate;
  name: string;
  visibleColumns: Set<AttendanceColumnKey>;
}) => {
  return (
    <tr className="bg-[#fbfcff] text-[#526078]">
      <TableCell>
        {name && (
          <span
            title={name}
            className="block max-w-[220px] truncate pl-6 font-bold"
          >
            {name}
          </span>
        )}
      </TableCell>
      {visibleColumns.has("dateSummary") && (
        <TableCell>
          <div className="flex items-center gap-2 pl-6 font-semibold">
            <FiCalendar className="h-3.5 w-3.5 text-[#98a2b3]" />
            {dateLabel}
          </div>
        </TableCell>
      )}
      <MetricCells metrics={metrics} visibleColumns={visibleColumns} />
    </tr>
  );
};

const MetricCells = ({
  metrics,
  visibleColumns
}: {
  metrics: Omit<AttendanceVehicle, "dates" | "vehicleName"> | AttendanceDate;
  visibleColumns: Set<AttendanceColumnKey>;
}) => {
  const percentage = toNumber(metrics.attendancePercentage);
  const status = getAttendanceStatus(percentage);

  return (
    <>
      {visibleColumns.has("assignedUsers") && (
        <TableCell align="right">
          {formatNumber(metrics.assignedUsers)}
        </TableCell>
      )}
      {visibleColumns.has("presentUsers") && (
        <TableCell align="right" className="text-[#16833a]">
          {formatNumber(metrics.presentUsers)}
        </TableCell>
      )}
      {visibleColumns.has("unAssignedUsers") && (
        <TableCell align="right" className="text-[#f28a03]">
          {formatNumber(metrics.unAssignedUsers)}
        </TableCell>
      )}
      {visibleColumns.has("absentUsers") && (
        <TableCell align="right" className="text-[#f04455]">
          {formatNumber(metrics.absentUsers)}
        </TableCell>
      )}
      {visibleColumns.has("attendancePercentage") && (
        <TableCell>
          <div className="flex min-w-[130px] items-center gap-2">
            <span className="w-12 text-right font-bold">
              {formatPercentage(percentage)}
            </span>
            <span className="h-1.5 flex-1 rounded-full bg-[#dbe4f0]">
              <span
                className="block h-1.5 rounded-full bg-[#2563eb]"
                style={{width: `${clampPercentage(percentage)}%`}}
              />
            </span>
          </div>
        </TableCell>
      )}
      {visibleColumns.has("status") && (
        <TableCell>
          <span
            className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold ${status.className}`}
          >
            {status.label}
          </span>
        </TableCell>
      )}
    </>
  );
};

const HeaderWithInfo = ({
  ariaLabel,
  content,
  label
}: {
  ariaLabel: string;
  content: ReactNode;
  label: string;
}) => {
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      <Tooltip
        showArrow
        placement="top"
        content={
          <div className="max-w-[260px] px-1 py-1 text-xs font-semibold leading-5">
            {content}
          </div>
        }
      >
        <span
          aria-label={ariaLabel}
          className="inline-flex h-4 w-4 cursor-help items-center justify-center  text-[#667085]"
          role="button"
          tabIndex={0}
        >
          <IoInformationCircle className="h-4 w-4" />
        </span>
      </Tooltip>
    </span>
  );
};

const TrendStatusHeader = () => {
  return (
    <HeaderWithInfo
      ariaLabel="Trend status rules"
      label="Trend / Status"
      content={
        <div className="space-y-1">
          <div>Below 50% - Need Improvement</div>
          <div>Above 50% - Good</div>
          <div>Above 70% - Very Good</div>
          <div>Above 90% - Excellent</div>
        </div>
      }
    />
  );
};

const TableHead = ({
  align = "left",
  children
}: {
  align?: "center" | "left" | "right";
  children: ReactNode;
}) => {
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";

  return (
    <th
      className={`border-b border-[#e5eaf3] px-3 py-3 text-xs font-bold ${alignClass}`}
    >
      {children}
    </th>
  );
};

const TableCell = ({
  align = "left",
  children,
  className = ""
}: {
  align?: "center" | "left" | "right";
  children: ReactNode;
  className?: string;
}) => {
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "";

  return (
    <td
      className={`border-b border-[#eef2f7] px-3 py-3 align-middle font-semibold ${alignClass} ${className}`}
    >
      {children}
    </td>
  );
};

const EmptyState = ({message}: {message: string}) => {
  return (
    <div className="flex min-h-48 items-center justify-center px-4 py-8 text-center text-sm font-semibold text-[#667085]">
      {message}
    </div>
  );
};
